import {
  Stack,
  Divider,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import { Person as PersonIcon } from "@mui/icons-material";
import {
  useWebApiV1OrderQuery,
  useWebApiV1ListAttachmentsQuery,
  useAutomationIntegrationApiV1GetPlanGenerationQuery,
  useWebApiV1UpdateOrderMutation,
} from "@providers/hop-ord-server/api";
import { useParams, useNavigate } from "react-router-dom";
import { useState } from "react";
import { skipToken } from "@reduxjs/toolkit/query";
import BottomActionBar from "@pages/Layout/BottomActionBar";
import { useCheckUserRole } from "@utils";
import { Role, OrderStatus } from "@enums";
import { toLocaleDateTimeString } from "@utils";
import { getCompletedPlanTimestamps } from "@volatile/pages/OrderTracker/components";
import {
  SinglePlanView,
  ComparisonView,
} from "@volatile/pages/OrderTracker/pages";

// Centralized plan configuration to eliminate duplication
const PLAN_CONFIG = {
  planA: { label: "Prostate Nodes R1", shortId: "planA" },
  planB: { label: "Prostate Nodes R2", shortId: "planB" },
} as const;

// Plan selection types and utilities
export interface PlanOption {
  value: string;
  label: string;
}

export const planOptions: PlanOption[] = Object.entries(PLAN_CONFIG).map(
  ([value, config]) => ({
    value,
    label: config.label,
  }),
);

export const usePlanSelection = () => {
  // Single plan mode state
  const [selectedPlanOption, setSelectedPlanOption] = useState("planA");

  // Comparison mode state
  const [selectedPlan, setSelectedPlan] = useState("planA");
  const [selectedPlanAOption, setSelectedPlanAOption] = useState("planA");
  const [selectedPlanBOption, setSelectedPlanBOption] = useState("planB");

  const getPlanDisplayName = (plan: string) => {
    // First check if it's a full plan key
    if (plan in PLAN_CONFIG) {
      return PLAN_CONFIG[plan as keyof typeof PLAN_CONFIG].label;
    }

    // Then check if it's a short ID
    const planEntry = Object.entries(PLAN_CONFIG).find(
      ([_, config]) => config.shortId === plan,
    );

    if (planEntry) {
      return planEntry[1].label;
    }

    // Default fallback
    return "Prostate VMAT";
  };

  // Filter available options based on current selections in comparison mode
  const getAvailablePlanAOptions = () => {
    return planOptions.filter((option) => option.value !== selectedPlanBOption);
  };

  const getAvailablePlanBOptions = () => {
    return planOptions.filter((option) => option.value !== selectedPlanAOption);
  };

  return {
    // Single mode
    selectedPlanOption,
    setSelectedPlanOption,

    // Comparison mode
    selectedPlan,
    setSelectedPlan,
    selectedPlanAOption,
    setSelectedPlanAOption,
    selectedPlanBOption,
    setSelectedPlanBOption,

    // Shared utilities
    planOptions,
    getPlanDisplayName,
    getAvailablePlanAOptions,
    getAvailablePlanBOptions,
  };
};

// Format DOB to US format with 3-letter month and dashes
const formatDobToUS = (dob: string | null) => {
  if (!dob) return "N/A";

  try {
    const date = new Date(dob);
    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "short",
      day: "2-digit",
    };
    return date
      .toLocaleDateString("en-US", options)
      .replace(/,\s/g, "-")
      .replace(/\s/g, "-");
  } catch {
    return dob; // Return original if parsing fails
  }
};

// Send for Review Confirmation Modal Component
const SendForReviewModal = ({
  open,
  onClose,
  onConfirm,
  selectedPlan,
  getPlanDisplayName,
  patientName,
  patientMrn,
  patientDob,
  planGenerationData: _planGenerationData,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  selectedPlan: string;
  getPlanDisplayName: (plan: string) => string;
  patientName: string | null;
  patientMrn: string | null;
  patientDob: string | null;
  planGenerationData: unknown[] | undefined;
}) => {
  // Function to get the timestamp for the selected plan
  const getPlanTimestamp = (planKey: string) => {
    // Always use localStorage timestamps to match CompletedPlans table (ignore planGenerationData)
    const timestamps = getCompletedPlanTimestamps();

    // Explicit mapping to ensure consistency with CompletedPlans table
    // planA → "Prostate Nodes R1" → plan1FinishedAt
    // planB → "Prostate Nodes R2" → plan2FinishedAt
    if (planKey === "planA") {
      return toLocaleDateTimeString(timestamps.plan1FinishedAt);
    } else if (planKey === "planB") {
      return toLocaleDateTimeString(timestamps.plan2FinishedAt);
    }

    // Fallback by plan display name
    const planDisplayName = getPlanDisplayName(planKey);
    if (planDisplayName === "Prostate Nodes R1") {
      return toLocaleDateTimeString(timestamps.plan1FinishedAt);
    } else if (planDisplayName === "Prostate Nodes R2") {
      return toLocaleDateTimeString(timestamps.plan2FinishedAt);
    }

    return "Mar-17-2024, 3:44 PM"; // Final fallback
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          padding: 1,
        },
      }}
    >
      <DialogTitle sx={{ paddingBottom: 1 }}>
        <Typography variant="h6">Send for review?</Typography>
      </DialogTitle>

      <DialogContent sx={{ paddingTop: 1, paddingBottom: 2 }}>
        <Stack gap={3}>
          <Typography variant="body1">
            Are you sure you want to send the following plan for Physician
            approval?
          </Typography>

          <Stack gap={1}>
            <Typography variant="body1">
              {getPlanDisplayName(selectedPlan)}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Created: {getPlanTimestamp(selectedPlan)}
            </Typography>
          </Stack>

          <Divider />

          <Stack direction="row" alignItems="center" gap={2}>
            <Stack
              width={24}
              height={24}
              borderRadius={1}
              bgcolor="primary.main"
              alignItems="center"
              justifyContent="center"
            >
              <PersonIcon sx={{ color: "white", fontSize: 16 }} />
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2">
                {patientName || "Unknown Patient"}
              </Typography>
              <Typography variant="body2" sx={{ mx: 2 }}>
                Patient ID: {patientMrn || "N/A"}
              </Typography>
              <Typography variant="body2">
                DOB: {formatDobToUS(patientDob)}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ padding: 2, paddingTop: 0 }}>
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{ textTransform: "uppercase" }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          sx={{ textTransform: "uppercase" }}
        >
          Send
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const DosimetristSendForReviewButton = ({
  orderId,
  isSubmitting,
  onSendForReview,
}: {
  orderId: number;
  isSubmitting: boolean;
  onSendForReview: () => void;
}) => {
  const isDosimetrist = useCheckUserRole(Role.DOSIMETRIST);
  const navigate = useNavigate();

  // Only show for dosimetrists
  if (!isDosimetrist) {
    return null;
  }

  return (
    <BottomActionBar>
      <Stack
        direction="row"
        width={1}
        justifyContent="flex-end"
        padding={2}
        gap={2}
        sx={{
          position: "relative",
          backgroundColor: "white",
          borderTop: "1px solid",
          borderColor: "divider",
        }}
      >
        <Button
          variant="outlined"
          color="primary"
          onClick={() => navigate(`/commandcenter/${orderId}/automated`)}
        >
          Back: Plan Generation
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={isSubmitting}
          startIcon={<SendIcon />}
          onClick={onSendForReview}
        >
          Send for Review
        </Button>
      </Stack>
    </BottomActionBar>
  );
};

interface PlanReviewPageProps {
  isComparisonMode: boolean;
}

const PlanReviewPage = ({ isComparisonMode }: PlanReviewPageProps) => {
  const { orderId } = useParams();
  const [sendForReviewModalOpen, setSendForReviewModalOpen] = useState(false);
  const [openToast, setOpenToast] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const planSelection = usePlanSelection();

  const [orderStatusUpdate] = useWebApiV1UpdateOrderMutation();
  const { data: orderData } = useWebApiV1OrderQuery({
    orderId: Number(orderId),
  });

  const { isLoading: scorecardAttachmentsLoading } =
    useWebApiV1ListAttachmentsQuery(
      orderId !== undefined
        ? {
            orderId: Number(orderId),
          }
        : skipToken,
    );

  // Fetch plan generation data to get the correct timestamps
  const { data: planGenerationData } =
    useAutomationIntegrationApiV1GetPlanGenerationQuery({
      orderId: Number(orderId),
    });

  const handleSendForReview = () => {
    setSendForReviewModalOpen(true);
  };

  const handleConfirmSendForReview = async () => {
    // Immediately close the dialog
    setSendForReviewModalOpen(false);
    setIsSubmitting(true);

    try {
      // Execute both operations
      await Promise.all([
        orderStatusUpdate({
          orderId: Number(orderId),
          orderIn: { status: OrderStatus.READY_FOR_RO_APPROVAL },
        }).unwrap(),
      ]);

      // Show success message after completion
      setOpenToast(true);
    } catch (error) {
      console.error("Error submitting for review:", error);
      // Handle error (you might want to show an error message to the user)
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseAlert = () => {
    setOpenToast(false);
  };

  // Get the currently selected plan based on the mode
  const currentlySelectedPlan = isComparisonMode
    ? planSelection.selectedPlan === "planA"
      ? planSelection.selectedPlanAOption
      : planSelection.selectedPlanBOption
    : planSelection.selectedPlanOption;

  return (
    <>
      <Stack gap={2} sx={{ marginBottom: 2 }}>
        {/* Route to appropriate view based on mode */}
        {isComparisonMode ? (
          <ComparisonView
            orderId={Number(orderId)}
            scorecardAttachmentsLoading={scorecardAttachmentsLoading}
            planSelection={planSelection}
            planGenerationData={planGenerationData}
            isPhysicianReview={false}
          />
        ) : (
          <SinglePlanView
            orderId={Number(orderId)}
            orderData={orderData}
            scorecardAttachmentsLoading={scorecardAttachmentsLoading}
            planSelection={planSelection}
            planGenerationData={planGenerationData}
          />
        )}

        {/* Send for Review Button */}
        <DosimetristSendForReviewButton
          orderId={Number(orderId)}
          isSubmitting={isSubmitting}
          onSendForReview={handleSendForReview}
        />
      </Stack>

      <SendForReviewModal
        open={sendForReviewModalOpen}
        onClose={() => setSendForReviewModalOpen(false)}
        onConfirm={handleConfirmSendForReview}
        selectedPlan={currentlySelectedPlan}
        getPlanDisplayName={planSelection.getPlanDisplayName}
        patientName={orderData?.patientName ?? null}
        patientMrn={orderData?.patientMrn ?? null}
        patientDob={orderData?.patientDob ?? null}
        planGenerationData={planGenerationData}
      />

      <Snackbar
        open={openToast}
        autoHideDuration={4000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        sx={{ "& .MuiPaper-root": { backgroundColor: "#2E3132" } }}
      >
        <Alert
          onClose={handleCloseAlert}
          severity="success"
          sx={{ width: "100%", backgroundColor: "#2E3132", color: "#fff" }}
        >
          Success! Plans have been sent to the physician for review
        </Alert>
      </Snackbar>
    </>
  );
};

export default PlanReviewPage;
